import { Head, router } from "@inertiajs/react"
import React, { useState, useCallback } from "react"
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts"
import AdminLayout from "@/components/admin/layout"
import Pagination from "@/components/pagination"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { PLATFORM_LABELS } from "@/lib/social-post-constants"

// --- Types ---

type AggregateMetrics = {
  views: number
  likes: number
  comments: number
  shares: number
  reach: number
  saves: number
  engagement_rate: number
  post_count: number
}

type PlatformBreakdown = {
  platform: string
  views: number
  likes: number
  comments: number
  shares: number
  post_count: number
}

type AccountBreakdown = {
  account_name: string
  views: number
  likes: number
  comments: number
  shares: number
  post_count: number
}

type TimeSeriesPoint = {
  date: string
  views: number
  likes: number
  comments: number
  shares: number
  posts: number
}

type AdminPost = {
  id: number
  platform: string
  caption: string | null
  account_name: string | null
  status: string
  post_kind: string
  views: number
  likes: number
  published_at: string | null
  created_at: string
  platform_post_url: string | null
}

interface PaginationMeta {
  page: number
  limit: number
  pages: number
  count: number
  from: number
  to: number
  prev: number | null
  next: number | null
}

interface Props {
  aggregate_metrics: AggregateMetrics
  platform_breakdown: PlatformBreakdown[]
  time_series: TimeSeriesPoint[]
  account_breakdown: AccountBreakdown[]
  analytics_filters: { days: number; platform: string | null }
  posts: AdminPost[]
  pagination: PaginationMeta
  search: string | null
  status: string | null
  platform_filter: string | null
  post_kind: string | null
  sort: string
  direction: string
  counts: {
    all: number
    pending: number
    scheduled: number
    published: number
    failed: number
  }
}

// --- Constants ---

const STATUS_OPTIONS = [
  { value: "all", label: "All statuses" },
  { value: "pending", label: "Pending" },
  { value: "scheduled", label: "Scheduled" },
  { value: "published", label: "Published" },
  { value: "failed", label: "Failed" },
] as const

const PLATFORM_OPTIONS = [
  { value: "all", label: "All platforms" },
  { value: "instagram", label: "Instagram" },
  { value: "facebook", label: "Facebook" },
  { value: "tiktok", label: "TikTok" },
  { value: "youtube", label: "YouTube" },
] as const

const KIND_OPTIONS = [
  { value: "all", label: "All kinds" },
  { value: "video", label: "Video" },
  { value: "listing", label: "Listing" },
  { value: "standalone", label: "Post" },
] as const

const STATUS_COLOURS: Record<string, string> = {
  pending: "bg-cream/10 text-cream/60 border-cream/20",
  scheduled: "bg-cream/10 text-cream/60 border-cream/20",
  uploading: "bg-cream/10 text-cream/60 border-cream/20",
  published: "bg-red/10 text-red border-red/20",
  failed: "bg-charcoal/50 text-cream/50 border-cream/10",
}

const KIND_COLOURS: Record<string, string> = {
  video: "bg-red/10 text-red border-red/20",
  listing: "bg-cream/10 text-cream/60 border-cream/20",
  standalone: "bg-cream/10 text-cream/60 border-cream/20",
}

const PLATFORM_HEX: Record<string, string> = {
  instagram: "#E1306C",
  facebook: "#1877F2",
  tiktok: "#FFFFFF",
  youtube: "#FF0000",
}

const ENGAGEMENT_COLOURS = {
  views: { stroke: "#C41E2A", label: "Views" },
  likes: { stroke: "#D4A853", label: "Likes" },
  comments: { stroke: "#8E8E93", label: "Comments" },
  shares: { stroke: "#F5F2ED", label: "Shares" },
} as const

const COLUMNS = [
  { key: "platform", label: "Platform", sortable: false },
  { key: "caption", label: "Post", sortable: false },
  { key: "account", label: "Account", sortable: false },
  { key: "status", label: "Status", sortable: false },
  { key: "post_kind", label: "Kind", sortable: false },
  { key: "views", label: "Views", sortable: true },
  { key: "likes", label: "Likes", sortable: false },
  { key: "published_at", label: "Published", sortable: true },
  { key: "created_at", label: "Created", sortable: true },
] as const

type ChartTab = "engagement" | "platform" | "accounts"

// --- Helpers ---

function formatCompact(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}m`
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`
  return String(n)
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  })
}

// --- Main Component ---

export default function AdminPostsIndex({
  aggregate_metrics,
  platform_breakdown,
  time_series,
  account_breakdown,
  analytics_filters,
  posts,
  pagination,
  search,
  status,
  platform_filter,
  post_kind,
  sort,
  direction,
  counts,
}: Props) {
  const [searchValue, setSearchValue] = useState(search || "")
  const [chartTab, setChartTab] = useState<ChartTab>("engagement")

  // --- Analytics filter handlers ---
  function updateAnalyticsFilter(key: string, value: string | null) {
    const params: Record<string, string> = { days: String(analytics_filters.days) }
    if (analytics_filters.platform) params.platform = analytics_filters.platform
    // Preserve table filters
    if (search) params.search = search
    if (status) params.status = status
    if (platform_filter) params.platform_filter = platform_filter
    if (post_kind) params.post_kind = post_kind
    if (sort !== "created_at") params.sort = sort
    if (direction !== "desc") params.direction = direction

    if (value) {
      params[key] = value
    } else {
      delete params[key]
    }
    router.get("/admin/posts", params, { preserveState: true, preserveScroll: true })
  }

  // --- Table filter handlers ---
  const buildParams = useCallback(
    (overrides: Record<string, string | undefined> = {}) => {
      const p: Record<string, string> = {}
      // Analytics filters
      if (analytics_filters.days !== 30) p.days = String(analytics_filters.days)
      if (analytics_filters.platform) p.platform = analytics_filters.platform
      // Table filters
      if (search) p.search = search
      if (status) p.status = status
      if (platform_filter) p.platform_filter = platform_filter
      if (post_kind) p.post_kind = post_kind
      if (sort !== "created_at") p.sort = sort
      if (direction !== "desc") p.direction = direction
      for (const [k, v] of Object.entries(overrides)) {
        if (v === undefined || v === "") {
          delete p[k]
        } else {
          p[k] = v
        }
      }
      return p
    },
    [search, status, platform_filter, post_kind, sort, direction, analytics_filters],
  )

  function handleSearch(e: React.FormEvent) {
    e.preventDefault()
    router.get(
      "/admin/posts",
      buildParams({ search: searchValue || undefined, page: undefined }),
      { preserveState: true },
    )
  }

  function handleSort(column: string) {
    const newDirection = sort === column && direction === "asc" ? "desc" : "asc"
    router.get(
      "/admin/posts",
      buildParams({ sort: column, direction: newDirection, page: undefined }),
      { preserveState: true },
    )
  }

  function handleFilter(key: string, value: string) {
    const val = value === "all" ? undefined : value
    router.get("/admin/posts", buildParams({ [key]: val, page: undefined }), {
      preserveState: true,
    })
  }

  const hasAnalyticsData = aggregate_metrics.post_count > 0

  return (
    <AdminLayout>
      <Head title="Posts | Admin" />
      <div className="space-y-8">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-display font-semibold text-cream">Posts</h1>
          <p className="mt-1 text-sm text-cream/50">
            Social posts across all accounts
          </p>
        </div>

        {/* === Analytics Section === */}
        <div className="space-y-6">
          {/* Analytics filters */}
          <div className="flex items-center gap-2">
            <Select
              value={analytics_filters.platform || "all"}
              onValueChange={(v) => updateAnalyticsFilter("platform", v === "all" ? null : v)}
            >
              <SelectTrigger className="w-36 border-white/10 bg-white/5 text-cream text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="dark border-white/10 bg-charcoal">
                {PLATFORM_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={String(analytics_filters.days)}
              onValueChange={(v) => updateAnalyticsFilter("days", v)}
            >
              <SelectTrigger className="w-28 border-white/10 bg-white/5 text-cream text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="dark border-white/10 bg-charcoal">
                <SelectItem value="7">7 days</SelectItem>
                <SelectItem value="30">30 days</SelectItem>
                <SelectItem value="90">90 days</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {hasAnalyticsData ? (
            <>
              {/* Metric cards */}
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
                <MetricCard label="Posts" value={formatCompact(aggregate_metrics.post_count)} />
                <MetricCard label="Views" value={formatCompact(aggregate_metrics.views)} />
                <MetricCard label="Likes" value={formatCompact(aggregate_metrics.likes)} />
                <MetricCard label="Shares" value={formatCompact(aggregate_metrics.shares)} />
                <MetricCard label="Comments" value={formatCompact(aggregate_metrics.comments)} />
                <MetricCard label="Engagement" value={`${aggregate_metrics.engagement_rate}%`} />
              </div>

              {/* Tabbed chart */}
              <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-5">
                <div className="mb-4 flex items-center justify-between">
                  <div className="flex gap-1">
                    {(["engagement", "platform", "accounts"] as ChartTab[]).map((tab) => (
                      <button
                        key={tab}
                        type="button"
                        onClick={() => setChartTab(tab)}
                        className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                          chartTab === tab
                            ? "bg-red text-white"
                            : "text-cream/50 hover:bg-white/5 hover:text-cream"
                        }`}
                      >
                        {tab === "engagement" ? "Engagement" : tab === "platform" ? "By Platform" : "Top Accounts"}
                      </button>
                    ))}
                  </div>

                  {chartTab === "engagement" && (
                    <div className="flex gap-4">
                      {(Object.entries(ENGAGEMENT_COLOURS) as [string, { stroke: string; label: string }][]).map(
                        ([key, { stroke, label }]) => (
                          <div key={key} className="flex items-center gap-1.5">
                            <span className="size-2 rounded-full" style={{ backgroundColor: stroke }} />
                            <span className="text-[11px] text-cream/40">{label}</span>
                          </div>
                        ),
                      )}
                    </div>
                  )}
                </div>

                {chartTab === "engagement" ? (
                  <EngagementChart data={time_series} />
                ) : chartTab === "platform" ? (
                  <PlatformChart data={platform_breakdown} />
                ) : (
                  <AccountsChart data={account_breakdown} />
                )}
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center rounded-xl border border-white/[0.06] bg-white/[0.02] px-8 py-16 text-center">
              <div className="mb-4 flex size-16 items-center justify-center rounded-full bg-white/5">
                <svg className="size-8 text-cream/30" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 0 1 3 19.875v-6.75ZM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V8.625ZM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V4.125Z" />
                </svg>
              </div>
              <h2 className="mb-2 font-display text-xl font-semibold text-cream">
                No published posts yet
              </h2>
              <p className="max-w-sm text-cream/50">
                Published posts across all accounts will appear here with analytics.
              </p>
            </div>
          )}
        </div>

        {/* === Table Section === */}
        <div className="space-y-4">
          <h2 className="text-lg font-display font-semibold text-cream">All Posts</h2>

          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-wrap gap-2">
              <Select value={status || "all"} onValueChange={(v) => handleFilter("status", v)}>
                <SelectTrigger className="w-[150px] border-white/10 bg-white/5 text-cream">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="dark border-white/10 bg-charcoal">
                  {STATUS_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                      {opt.value !== "all" && (
                        <span className="ml-1 text-[10px] opacity-50">
                          {counts[opt.value as keyof typeof counts]}
                        </span>
                      )}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={platform_filter || "all"} onValueChange={(v) => handleFilter("platform_filter", v)}>
                <SelectTrigger className="w-[150px] border-white/10 bg-white/5 text-cream">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="dark border-white/10 bg-charcoal">
                  {PLATFORM_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={post_kind || "all"} onValueChange={(v) => handleFilter("post_kind", v)}>
                <SelectTrigger className="w-[130px] border-white/10 bg-white/5 text-cream">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="dark border-white/10 bg-charcoal">
                  {KIND_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <form onSubmit={handleSearch} className="flex gap-2">
              <div className="relative">
                <SearchIcon className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-cream/30" />
                <Input
                  placeholder="Search caption or account..."
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  className="h-8 w-56 border-white/10 bg-white/5 pl-8 text-sm text-cream placeholder:text-cream/30"
                />
              </div>
              {search && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="text-cream/50 hover:text-cream"
                  onClick={() => {
                    setSearchValue("")
                    router.get(
                      "/admin/posts",
                      buildParams({ search: undefined, page: undefined }),
                      { preserveState: true },
                    )
                  }}
                >
                  Clear
                </Button>
              )}
            </form>
          </div>

          <div className="overflow-x-auto rounded-lg border border-white/[0.06]">
            <table className="w-full min-w-[900px] text-sm">
              <thead>
                <tr className="border-b border-white/[0.06] bg-white/[0.02] text-left">
                  {COLUMNS.map((col) => (
                    <th
                      key={col.key}
                      className="px-4 py-2.5 text-xs font-medium uppercase tracking-wider text-cream/40"
                    >
                      {col.sortable ? (
                        <button
                          type="button"
                          className="inline-flex items-center gap-1 transition-colors hover:text-cream/70"
                          onClick={() => handleSort(col.key)}
                        >
                          {col.label}
                          <SortIcon
                            active={sort === col.key}
                            direction={sort === col.key ? direction : undefined}
                          />
                        </button>
                      ) : (
                        col.label
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {posts.length === 0 && (
                  <tr>
                    <td colSpan={COLUMNS.length} className="px-4 py-12 text-center text-cream/30">
                      {search ? `No posts matching "${search}"` : "No posts found"}
                    </td>
                  </tr>
                )}
                {posts.map((post) => (
                  <tr
                    key={post.id}
                    className="border-b border-white/[0.04] text-cream/70 transition-colors hover:bg-white/[0.04]"
                  >
                    <td className="whitespace-nowrap px-4 py-2.5">
                      <span className="inline-flex items-center gap-1.5 text-xs">
                        <PlatformIcon platform={post.platform} className="size-4 shrink-0" />
                        <span className="text-cream/50">{PLATFORM_LABELS[post.platform]}</span>
                      </span>
                    </td>
                    <td className="max-w-[200px] truncate px-4 py-2.5 text-cream">
                      {post.platform_post_url ? (
                        <a
                          href={post.platform_post_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="hover:text-red transition-colors"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {post.caption || "Untitled"}
                        </a>
                      ) : (
                        post.caption || "Untitled"
                      )}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5 text-cream/50">
                      {post.account_name || "\u2014"}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5">
                      <Badge className={STATUS_COLOURS[post.status] || STATUS_COLOURS.pending}>
                        {post.status}
                      </Badge>
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5">
                      <Badge className={KIND_COLOURS[post.post_kind] || KIND_COLOURS.video}>
                        {post.post_kind}
                      </Badge>
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5 text-xs tabular-nums text-cream/50">
                      {formatCompact(post.views)}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5 text-xs tabular-nums text-cream/50">
                      {formatCompact(post.likes)}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5 text-xs text-cream/50">
                      {post.published_at ? formatDate(post.published_at) : "\u2014"}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5 text-xs text-cream/50">
                      {formatDate(post.created_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Pagination baseUrl="/admin/posts" params={buildParams()} pagination={pagination} />
        </div>
      </div>
    </AdminLayout>
  )
}

// --- Chart Components ---

function EngagementChart({ data }: { data: TimeSeriesPoint[] }) {
  if (data.length === 0) {
    return (
      <div className="flex h-60 items-center justify-center text-sm text-cream/40">
        Not enough data to display a chart.
      </div>
    )
  }

  return (
    <div className="h-64">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 4, right: 4, bottom: 0, left: -20 }}>
          <defs>
            <linearGradient id="gViewsDark" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#C41E2A" stopOpacity={0.3} />
              <stop offset="100%" stopColor="#C41E2A" stopOpacity={0} />
            </linearGradient>
            <linearGradient id="gLikesDark" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#D4A853" stopOpacity={0.3} />
              <stop offset="100%" stopColor="#D4A853" stopOpacity={0} />
            </linearGradient>
            <linearGradient id="gCommentsDark" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#8E8E93" stopOpacity={0.3} />
              <stop offset="100%" stopColor="#8E8E93" stopOpacity={0} />
            </linearGradient>
            <linearGradient id="gSharesDark" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#F5F2ED" stopOpacity={0.2} />
              <stop offset="100%" stopColor="#F5F2ED" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
          <XAxis
            dataKey="date"
            tickFormatter={(d: string) => {
              const date = new Date(d)
              return date.toLocaleDateString("en-GB", { day: "numeric", month: "short" })
            }}
            tick={{ fontSize: 11, fill: "rgba(245,242,237,0.4)" }}
            axisLine={false}
            tickLine={false}
            interval="preserveStartEnd"
          />
          <YAxis
            tickFormatter={formatCompact}
            tick={{ fontSize: 11, fill: "rgba(245,242,237,0.4)" }}
            axisLine={false}
            tickLine={false}
            allowDecimals={false}
          />
          <Tooltip content={<DarkEngagementTooltip />} />
          <Area type="monotone" dataKey="views" stroke="#C41E2A" fill="url(#gViewsDark)" strokeWidth={1.5} dot={false} />
          <Area type="monotone" dataKey="likes" stroke="#D4A853" fill="url(#gLikesDark)" strokeWidth={1.5} dot={false} />
          <Area type="monotone" dataKey="comments" stroke="#8E8E93" fill="url(#gCommentsDark)" strokeWidth={1.5} dot={false} />
          <Area type="monotone" dataKey="shares" stroke="#F5F2ED" fill="url(#gSharesDark)" strokeWidth={1.5} dot={false} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  )
}

function DarkEngagementTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean
  payload?: Array<{ name: string; value: number; color: string }>
  label?: string
}) {
  if (!active || !payload?.length) return null

  const formattedDate = label
    ? new Date(String(label)).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : ""

  return (
    <div className="rounded-lg border border-white/10 bg-charcoal px-3 py-2 shadow-md">
      {formattedDate && (
        <p className="mb-1.5 text-[11px] font-medium text-cream/50">{formattedDate}</p>
      )}
      {payload.map((entry) => (
        <div key={entry.name} className="flex items-center gap-2 text-xs">
          <span className="size-1.5 rounded-full" style={{ backgroundColor: entry.color }} />
          <span className="capitalize text-cream/50">{entry.name}</span>
          <span className="ml-auto tabular-nums font-medium text-cream">
            {formatCompact(entry.value)}
          </span>
        </div>
      ))}
    </div>
  )
}

function PlatformChart({ data }: { data: PlatformBreakdown[] }) {
  if (data.length === 0) {
    return (
      <div className="flex h-60 items-center justify-center text-sm text-cream/40">
        No platform data available.
      </div>
    )
  }

  const chartData = data
    .map((pb) => ({
      platform: PLATFORM_LABELS[pb.platform] || pb.platform,
      post_count: pb.post_count,
      views: pb.views,
      colour: PLATFORM_HEX[pb.platform] || "#3A3A3C",
    }))
    .sort((a, b) => b.post_count - a.post_count)

  return (
    <div className="h-64">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={chartData} margin={{ top: 4, right: 4, bottom: 0, left: -20 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" vertical={false} />
          <XAxis
            dataKey="platform"
            tick={{ fontSize: 11, fill: "rgba(245,242,237,0.4)" }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            tick={{ fontSize: 11, fill: "rgba(245,242,237,0.4)" }}
            axisLine={false}
            tickLine={false}
            allowDecimals={false}
          />
          <Tooltip
            content={<DarkPlatformTooltip />}
            cursor={{ fill: "rgba(255,255,255,0.03)" }}
          />
          <Bar dataKey="post_count" radius={[4, 4, 0, 0]} barSize={40} name="Posts">
            {chartData.map((entry) => (
              <Cell key={entry.platform} fill={entry.colour} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  )
}

function DarkPlatformTooltip({
  active,
  payload,
}: {
  active?: boolean
  payload?: Array<{
    payload: { platform: string; post_count: number; views: number; colour: string }
  }>
}) {
  if (!active || !payload?.length) return null

  const d = payload[0].payload
  return (
    <div className="rounded-lg border border-white/10 bg-charcoal px-3 py-2 shadow-md">
      <p className="mb-1 text-[11px] font-medium text-cream/50">{d.platform}</p>
      <div className="flex items-center gap-2 text-xs">
        <span className="size-1.5 rounded-full" style={{ backgroundColor: d.colour }} />
        <span className="text-cream/50">Posts</span>
        <span className="ml-auto tabular-nums font-medium text-cream">{d.post_count}</span>
      </div>
      <div className="flex items-center gap-2 text-xs">
        <span className="size-1.5 rounded-full" style={{ backgroundColor: d.colour, opacity: 0.5 }} />
        <span className="text-cream/50">Views</span>
        <span className="ml-auto tabular-nums font-medium text-cream">{formatCompact(d.views)}</span>
      </div>
    </div>
  )
}

function AccountsChart({ data }: { data: AccountBreakdown[] }) {
  if (data.length === 0) {
    return (
      <div className="flex h-60 items-center justify-center text-sm text-cream/40">
        No account data available.
      </div>
    )
  }

  const chartData = data.map((ab) => ({
    name: ab.account_name,
    views: ab.views,
    posts: ab.post_count,
  }))

  return (
    <div className="h-64">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={chartData} layout="vertical" margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" horizontal={false} />
          <XAxis
            type="number"
            tickFormatter={formatCompact}
            tick={{ fontSize: 11, fill: "rgba(245,242,237,0.4)" }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            type="category"
            dataKey="name"
            width={120}
            tick={{ fontSize: 11, fill: "rgba(245,242,237,0.5)" }}
            axisLine={false}
            tickLine={false}
          />
          <Tooltip
            content={<DarkAccountTooltip />}
            cursor={{ fill: "rgba(255,255,255,0.03)" }}
          />
          <Bar dataKey="views" fill="#C41E2A" radius={[0, 4, 4, 0]} barSize={20} name="Views" />
        </BarChart>
      </ResponsiveContainer>
    </div>
  )
}

function DarkAccountTooltip({
  active,
  payload,
}: {
  active?: boolean
  payload?: Array<{
    payload: { name: string; views: number; posts: number }
  }>
}) {
  if (!active || !payload?.length) return null

  const d = payload[0].payload
  return (
    <div className="rounded-lg border border-white/10 bg-charcoal px-3 py-2 shadow-md">
      <p className="mb-1 text-[11px] font-medium text-cream/50">{d.name}</p>
      <div className="flex items-center gap-2 text-xs">
        <span className="size-1.5 rounded-full bg-red" />
        <span className="text-cream/50">Views</span>
        <span className="ml-auto tabular-nums font-medium text-cream">{formatCompact(d.views)}</span>
      </div>
      <div className="flex items-center gap-2 text-xs">
        <span className="size-1.5 rounded-full bg-cream/30" />
        <span className="text-cream/50">Posts</span>
        <span className="ml-auto tabular-nums font-medium text-cream">{d.posts}</span>
      </div>
    </div>
  )
}

// --- UI Components ---

function MetricCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="group relative overflow-hidden rounded-lg border border-white/[0.06] bg-white/[0.03] p-5 transition-colors hover:bg-white/[0.06]">
      <p className="text-xs font-medium uppercase tracking-wider text-cream/40">
        {label}
      </p>
      <p className="mt-2 text-3xl font-semibold tabular-nums text-cream">
        {value}
      </p>
      <div className="pointer-events-none absolute -right-3 -top-3 size-16 rounded-full bg-white/[0.02] transition-transform duration-300 group-hover:scale-150" />
    </div>
  )
}

function SortIcon({ active, direction }: { active: boolean; direction?: string }) {
  if (!active) {
    return (
      <svg className="size-3 opacity-30" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 15 12 18.75 15.75 15m-7.5-6L12 5.25 15.75 9" />
      </svg>
    )
  }
  return direction === "asc" ? (
    <svg className="size-3" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 15.75l7.5-7.5 7.5 7.5" />
    </svg>
  ) : (
    <svg className="size-3" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
    </svg>
  )
}

function SearchIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
    </svg>
  )
}

// --- Platform Icons ---

function InstagramIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="#E1306C">
      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
    </svg>
  )
}

function FacebookIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="#1877F2">
      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
    </svg>
  )
}

function TikTokIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="#F5F2ED">
      <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
    </svg>
  )
}

function YouTubeIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="#FF0000">
      <path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
    </svg>
  )
}

const PLATFORM_ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
  instagram: InstagramIcon,
  facebook: FacebookIcon,
  tiktok: TikTokIcon,
  youtube: YouTubeIcon,
}

function PlatformIcon({ platform, className }: { platform: string; className?: string }) {
  const Icon = PLATFORM_ICON_MAP[platform]
  if (Icon) return <Icon className={className} />
  return <span className={`inline-block shrink-0 rounded-full bg-cream/30 ${className}`} />
}
